import React from 'react';
import style from './stickyFooter.module.scss';
import { FormattedMessage } from 'react-intl';
import { NotificationIcon } from 'acscp/components/common/icons/notificationIcon';
import { PlaneIcon } from '../icons/planeIcon';
import { CallUsIcon } from '../icons/callUsIcon';
import { AppState } from 'acscp/store';
import { Dispatch } from 'redux';
import {
    userCanOnlyAccessLoyalty,
    userCanPotentiallyRequestFlights,
    userIsAuthenticated,
    userIsLoggedIn,
} from 'acscp/store/helpers/authHelper';
import { PresentationComponentBase } from 'acscp/components/presentationComponentBase';
import { NotificationButton } from 'acscp/components/common/notificationButton';
import classNames from 'classnames';
import { FetchRequest } from 'acscp/actions';
import { AccountNotificationsRequest } from 'acscp/api/requests';
import { getNumberOfUnreadNotifications } from 'acscp/store/selectors/accountNotificationSelector';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { newFlightRequest } from 'acscp/components/routeHelper';
import { CallUsButton } from 'acscp/components/common/callUsButton';
import { RouteComponentProps, withRouter } from 'react-router-dom';

interface StickyFooterStateProps {
    userIsLoggedIn: boolean;
    isAuthenticated: boolean;
    canOnlyAccessLoyalty: boolean;
    canPotentiallyRequestFlights: boolean;
    numberOfUnreadNotifications: number;
}

interface StickyFooterDispatchProps {
    fetchNotifications: () => void;
}

type StickyFooterProps = StickyFooterStateProps & StickyFooterDispatchProps;

export class StickyFooterPresentational extends PresentationComponentBase<
    RouteComponentProps<any>, StickyFooterStateProps, StickyFooterDispatchProps, {}> {

    enquireOnClick = () => this.props.history.push(newFlightRequest());

    componentDidMount(): void {
        if (this.props.isAuthenticated) {
            this.props.fetchNotifications();
        }
    }

    componentDidUpdate(prevProps: StickyFooterProps): void {
        if (!prevProps.isAuthenticated && this.props.isAuthenticated) {
            this.props.fetchNotifications();
        }
    }

    render(): JSX.Element | null {
        if (!ENABLE_CREATE_ACCOUNT && !this.props.userIsLoggedIn) {
            // If the user is not logged in, do not display the footer
            // (since there's no way for them to create an account after clicking one of the options)
            return null;
        }

        if (this.props.canOnlyAccessLoyalty) {
            // If user is logged in, but does not have the ability to access charters do not render the footer
            // This is as it will only have one item with a home link.
            return null;
        }

        const unreadNotifications = this.props.numberOfUnreadNotifications;
        const hasUnreadNotifications = unreadNotifications > 0;
        return (
            <div className={style.StickyFooter}>
                <div className={style.Menu}>
                    <CallUsButton bodyClass={style.StickyFooterItem}>
                        <div className={classNames(style.Icon)}>
                            <CallUsIcon />
                        </div>
                        <div className={style.Title}>
                            <FormattedMessage id="components.stickyFooter.callUs" defaultMessage="CALL US" />
                        </div>
                    </CallUsButton>
                    {this.props.canPotentiallyRequestFlights &&
                        <div className={style.StickyFooterItem} onClick={this.enquireOnClick}>
                            <div className={style.Icon} >
                                <PlaneIcon />
                            </div>
                            <div className={style.Title}>
                                <FormattedMessage id="components.stickyFooter.enquireNow" defaultMessage="ENQUIRE NOW" />
                            </div>
                        </div>
                    }
                    {this.props.isAuthenticated &&
                        <NotificationButton
                            buttonClass={classNames(
                                style.StickyFooterItem,
                                {
                                    [style.HasUnreadNotifications]: hasUnreadNotifications
                                })
                            }
                        >
                            <div className={classNames(style.NotificationsIcon, style.Icon)}>
                                <NotificationIcon />
                            </div>
                            <div className={style.NotificationNumber}>
                                {unreadNotifications > 99 ? '99+' : unreadNotifications}
                            </div>
                            <div className={style.Title}>
                                <FormattedMessage
                                    id={'components.stickyFooter.notifications'}
                                    defaultMessage={'NOTIFICATIONS'}
                                />
                            </div>
                        </NotificationButton>
                    }
                </div>
            </div>
        );
    }
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: {}): StickyFooterDispatchProps  {
    return {
        fetchNotifications: () => dispatch(new FetchRequest(new AccountNotificationsRequest())),
    };
}

function mapStateToProps(state: AppState, ownProps: {}): StickyFooterStateProps {
    return {
        userIsLoggedIn: userIsLoggedIn(state.auth),
        isAuthenticated: userIsAuthenticated(state.auth),
        canOnlyAccessLoyalty: userCanOnlyAccessLoyalty(state.auth),
        numberOfUnreadNotifications: getNumberOfUnreadNotifications(state),
        canPotentiallyRequestFlights: userCanPotentiallyRequestFlights(state.auth)
    };
}

export const StickyFooter = connectWithRequests(mapStateToProps, mapDispatchToProps)(withRouter(StickyFooterPresentational));
